"use client"

import * as React from "react"

import {
  consentCookieString,
  emitConsentUpdate,
  emitEvent,
  isTagReady,
  readConsentCookie,
  TAG_READY_EVENT,
  toConsentMode,
  type AnalyticsEvent,
  type ConsentChoice,
  type GtagWindow,
} from "@workspace/analytics"
import type { GoogleTag } from "@workspace/model"

/**
 * Per-tenant analytics context: the validated Google tag (or `null`), the
 * diner's consent choice and a `track` emitter. Mounted by the root layout's
 * TenantShell inside the provider tree, so every storefront route — including
 * the pizza/meal-deal wizards outside `(store)` — shares one instance.
 *
 * `track` sends regardless of consent: Consent Mode v2 (set up by the inline
 * bootstrap) makes Google drop or anonymise what the diner hasn't allowed, so
 * gating here would only lose the cookieless pings Consent Mode relies on.
 * Events tracked before the bootstrap has run (page effects fire before the
 * `afterInteractive` script) are queued and flushed on its ready event, so
 * none can reach Google ahead of the consent defaults.
 */
export interface AnalyticsContextValue {
  tag: GoogleTag | null
  /** `undefined` until the cookie is read on the client; `null` = undecided. */
  consent: ConsentChoice | null | undefined
  /** True while the diner reopened the banner from the footer link. */
  settingsOpen: boolean
  openSettings: () => void
  saveConsent: (choice: ConsentChoice) => void
  track: (event: AnalyticsEvent | null) => void
}

const NOOP_CONTEXT: AnalyticsContextValue = {
  tag: null,
  consent: undefined,
  settingsOpen: false,
  openSettings: () => {},
  saveConsent: () => {},
  track: () => {},
}

const AnalyticsContext =
  React.createContext<AnalyticsContextValue>(NOOP_CONTEXT)

// The consent cookie as an external store: SSR and the hydration pass see
// `null` (unknown), the client then reads `document.cookie`, and saving a
// choice notifies subscribers so every consumer re-reads it.
const consentListeners = new Set<() => void>()
function subscribeConsent(listener: () => void) {
  consentListeners.add(listener)
  return () => {
    consentListeners.delete(listener)
  }
}
const readCookieHeader = () => document.cookie
const readServerCookieHeader = () => null

/** `window` as the bootstrap leaves it (`dataLayer` + `gtag` + ready flag). */
const gtagWindow = () => window as unknown as GtagWindow

export function AnalyticsProvider({
  tag,
  children,
}: {
  tag: GoogleTag | null
  children: React.ReactNode
}) {
  const cookieHeader = React.useSyncExternalStore(
    subscribeConsent,
    readCookieHeader,
    readServerCookieHeader
  )
  const consent = React.useMemo(
    () => (cookieHeader === null ? undefined : readConsentCookie(cookieHeader)),
    [cookieHeader]
  )
  const [settingsOpen, setSettingsOpen] = React.useState(false)

  const saveConsent = React.useCallback((choice: ConsentChoice) => {
    document.cookie = consentCookieString(choice, {
      secure: window.location.protocol === "https:",
    })
    emitConsentUpdate(gtagWindow(), toConsentMode(choice))
    setSettingsOpen(false)
    for (const listener of consentListeners) listener()
  }, [])

  const openSettings = React.useCallback(() => setSettingsOpen(true), [])

  const pending = React.useRef<AnalyticsEvent[]>([])

  const track = React.useCallback(
    (event: AnalyticsEvent | null) => {
      if (!tag || !event) return
      if (isTagReady(gtagWindow())) emitEvent(gtagWindow(), tag, event)
      else pending.current.push(event)
    },
    [tag]
  )

  // Flush whatever was tracked before the bootstrap ran.
  React.useEffect(() => {
    if (!tag) return
    const flush = () => {
      const queued = pending.current.splice(0)
      for (const event of queued) emitEvent(gtagWindow(), tag, event)
    }
    if (isTagReady(gtagWindow())) flush()
    window.addEventListener(TAG_READY_EVENT, flush)
    return () => window.removeEventListener(TAG_READY_EVENT, flush)
  }, [tag])

  const value = React.useMemo<AnalyticsContextValue>(
    () => ({ tag, consent, settingsOpen, openSettings, saveConsent, track }),
    [tag, consent, settingsOpen, openSettings, saveConsent, track]
  )

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}

/** Analytics context; a no-op value outside the provider (e.g. dev gallery). */
export function useAnalytics(): AnalyticsContextValue {
  return React.useContext(AnalyticsContext)
}
